import { Flag } from '../../utils';

/**
 * Generates a list of all numeric bitmask values (0 to maxBitmask) that contain
 * at least one of the selected flag bits.
 *
 * Useful for filtering bitmask fields where each value may contain multiple combined flags.
 *
 * @param selectedFlags - Array of selected bit values (e.g., [1, 4])
 * @param maxBitmask - Maximum possible combined bitmask value (e.g., 31 for 5-bit flags)
 * @returns All bitmask values that include any of the selected flags
 *
 * @example
 * generateBitmaskFilters([1], 31)
 * // → [1, 3, 5, 7, 9, 11, 13, 15, 17, 19, 21, 23, 25, 27, 29, 31]
 *
 * @example
 * generateBitmaskFilters([1, 4], 31)
 * // → [1, 3, 4, 5, 6, 7, 9, 11, 12, 13, 14, 15, 17, 19, 20, 21, 22, 23, 25, 27, 28, 29, 30, 31]
 */

export const generateBitmaskFilters = (selectedFlags: number[], maxBitmask: number): number[] => {
  if (!selectedFlags.length) return [];
  const allFlags = [...Array(maxBitmask + 1).keys()];
  return allFlags.filter((value) => {
    if (value === 0 && selectedFlags.includes(0)) return true;

    return selectedFlags.some((selected) => selected !== 0 && (value & selected) === selected);
  });
};

/**
 * Creates a React-Admin `parse` function that transforms selected bit flag values
 * into a list of valid bitmask combinations for filtering.
 *
 * It ensures only known flag IDs are processed and returns undefined when input is empty or invalid.
 *
 * @param flags - Array of available flag objects (each with an `id` representing the bit value)
 * @returns A function that takes selected flags and returns valid filterable bitmask values
 *
 * @example
 * const flags = [{ id: 1, name: 'Success' }, { id: 4, name: 'Mapping Errors' }];
 * const parse = createBitmaskParser(flags);
 * parse([1])
 * // → [1, 3, 5, 7, 9, 11, 13, 15, 17, 19, 21, 23, 25, 27, 29, 31]
 */

export const createBitmaskParser =
  (flags: Flag[]) =>
  (selected: number[] | undefined): number[] | undefined => {
    if (!selected?.length) return undefined;
    const allFlagIds = flags.map((f) => f.id);
    const maxBitmask = allFlagIds.reduce((acc, flag) => acc | flag, 0);
    const validSelectedFlags = selected.filter((flag) => allFlagIds.includes(flag));
    if (validSelectedFlags.length === 0) return undefined;
    return generateBitmaskFilters(validSelectedFlags, maxBitmask);
  };
